import { useId, useState } from 'react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { AlertIcon, TrashIcon } from '../../components/icons';
import { describeApiError } from '../qrs/errors';
import { BULK_ACTIONS, CONFIRM_COUNT_FROM, MIN_REASON, type BulkAction } from './bulkActions';

export interface BulkSubmit {
  action: BulkAction;
  codes: string[];
  reason: string;
  prompt?: string;
}

export interface BulkActionModalProps {
  action: BulkAction;
  /** Códigos afectados; la cantidad se muestra en el título y en el botón. */
  codes: string[];
  onClose: () => void;
  /**
   * Ejecuta la acción. Todavía no existe el endpoint de lote (F4): la llamada vive acá
   * para conectarla sin tocar el modal.
   */
  onSubmit: (input: BulkSubmit) => Promise<void>;
}

/**
 * Patrón único de acción destructiva con motivo (design-spec §2), sea sobre uno o sobre mil códigos:
 * título con la cantidad, advertencia de exclusiones, motivo obligatorio con contador, y la cantidad
 * escrita a mano cuando el lote pasa de diez.
 */
export function BulkActionModal({ action, codes, onClose, onSubmit }: BulkActionModalProps) {
  const spec = BULK_ACTIONS[action];
  const n = codes.length;
  const id = useId();
  const [reason, setReason] = useState('');
  const [prompt, setPrompt] = useState('');
  const [countInput, setCountInput] = useState('');
  const [blurred, setBlurred] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reasonLength = reason.trim().length;
  const reasonOk = !spec.destructive || reasonLength >= MIN_REASON;
  const needsCount = n >= CONFIRM_COUNT_FROM;
  const countOk = !needsCount || countInput.trim() === String(n);
  const promptOk = !spec.needsPrompt || prompt.trim().length > 0;
  const canSubmit = reasonOk && countOk && promptOk;
  const showReasonError = blurred && !reasonOk;

  async function submit() {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit({ action, codes, reason: reason.trim(), prompt: spec.needsPrompt ? prompt.trim() : undefined });
      onClose();
    } catch (err) {
      setError(describeApiError(err, 'No pudimos aplicar la acción.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={spec.title(n)}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant={spec.destructive ? 'danger' : 'primary'} disabled={!canSubmit} loading={saving} onClick={() => void submit()}>
            {action === 'delete' && <TrashIcon />}
            {spec.cta(n)}
          </Button>
        </>
      }
    >
      {spec.warning && (
        <div className="flex gap-2 rounded-md border border-warning bg-white px-3 py-2 text-sm text-warning-text">
          <AlertIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{spec.warning}</span>
        </div>
      )}

      <p className="text-sm text-gray-700">
        {n === 1 ? (
          <>
            Código afectado: <span className="font-mono font-semibold tracking-wider">{codes[0]}</span>.
          </>
        ) : (
          <>
            Códigos afectados: <span className="font-mono">{codes.slice(0, 8).join(', ')}</span>
            {n > 8 && <span className="text-ink-muted"> y {n - 8} más</span>}.
          </>
        )}
      </p>

      {spec.needsPrompt && (
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-prompt`} className="text-xs font-medium text-ink-muted">
            Prompt para toda la selección
          </label>
          <input
            id={`${id}-prompt`}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Ej.: cordillera nevada al atardecer"
            className="w-full rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink"
          />
        </div>
      )}

      {spec.destructive && (
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-reason`} className="text-xs font-medium text-ink-muted">
            Motivo <span className="font-semibold text-error-text">obligatorio</span>
          </label>
          <textarea
            id={`${id}-reason`}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setBlurred(true)}
            aria-invalid={showReasonError || undefined}
            aria-describedby={`${id}-help${showReasonError ? ` ${id}-reason-error` : ''}`}
            className={`w-full rounded-sm border bg-white px-3 py-2 text-sm text-ink ${showReasonError ? 'border-error' : 'border-line'}`}
          />
          <span id={`${id}-help`} className="flex flex-wrap justify-between gap-2 text-xs text-ink-muted">
            <span>Queda en la auditoría de cada código, junto a tu nombre.</span>
            <span className="tabular-nums">
              {reasonLength}/{MIN_REASON} mínimo
            </span>
          </span>
          {showReasonError && (
            <p id={`${id}-reason-error`} role="alert" className="flex items-center gap-1.5 text-xs font-medium text-error-text">
              <AlertIcon className="h-3.5 w-3.5" />
              Escribe al menos {MIN_REASON} caracteres para continuar.
            </p>
          )}
        </div>
      )}

      {needsCount && (
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-count`} className="text-xs font-medium text-ink-muted">
            Escribe la cantidad para confirmar
          </label>
          <input
            id={`${id}-count`}
            inputMode="numeric"
            value={countInput}
            onChange={(e) => setCountInput(e.target.value)}
            placeholder={String(n)}
            aria-describedby={`${id}-count-help`}
            className="w-full rounded-sm border border-line bg-white px-3 py-2 text-sm tabular-nums text-ink"
          />
          <span id={`${id}-count-help`} className="text-xs text-ink-muted">
            Se pide desde {CONFIRM_COUNT_FROM} QR en adelante.
          </span>
        </div>
      )}

      {spec.note && <p className="rounded-md bg-mist px-3 py-2 text-xs text-ink-muted">{spec.note}</p>}
      {error && <Banner tone="error">{error}</Banner>}
    </Modal>
  );
}
